import GlobalAppPlugin from "./plugins/GlobalAppPlugin";
import EnvPlugin from "./plugins/EnvPlugin";
import MomentPlugin from "./plugins/MomentPlugin";
import AxiosPlugin from "./plugins/AxiosPlugin";
import TitlePlugin from "./plugins/TitlePlugin";
import DocumentReadyPlugin from "./plugins/DocumentReadyPlugin";
import ReactRenderPlugin from "./plugins/ReactRenderPlugin";

export default class App {

    constructor() {
        this._plugins = {
            register: [],
            boot: []
        };
        this._components = {};
        this._services = [];
        this._global_services = [];
        this._templates = {};
        this._defaultTemplate = null;
    }

    register(plugin) {
        this._plugins.register.push(plugin);
    }

    boot(plugin) {
        this._plugins.boot.push(plugin);
    }

    hasPlugins() {
        return this._plugins.length > 0;
    }

    setReactComponents(namespace, components) {
        this._components[namespace] = components;
    }

    templates(templates) {
        if (typeof (templates) === 'undefined') {
            return {...this._templates};
        }
        this._templates = {...this._templates, ...templates};
    }

    services(services) {
        if (typeof (services) === 'undefined') {
            return [...this._services];
        }
        this._services = [...this._services, ...services];
    }

    globalServices(services) {
        if (typeof (services) === 'undefined') {
            return [...this._global_services];
        }
        this._global_services = [...this._global_services, ...services];
    }

    defaultTemplate(templateName) {
        if (typeof (templateName) === 'undefined') {
            return this._defaultTemplate;
        }
        this._defaultTemplate = templateName;
    }

    get components() {
        return this._components;
    }

    get pluginsAtRegister() {
        return this._plugins.register;
    }

    get pluginsAtBoot() {
        return this._plugins.boot;
    }

    logInfo(text) {
        console.info('%c' + text, 'color: #4686b8');
    }

    logError(text) {
        console.info('%c' + text, 'color: #d95757');
    }

    bootNextPlugin(plugins) {

        if (plugins.length === 0) {
            return Promise.resolve();
        }

        let plugin = plugins.shift();

        return new Promise((resolve, reject) => {
            Promise
                .resolve(plugin(this))
                .then(() => {
                    //this.logInfo(' - ' + plugin.constructor.name);
                })
                .catch((err) => {
                    console.error(err);
                    //this.logError(' - ' + plugin.constructor.name);
                })
                .finally(() => {
                    return this.bootNextPlugin(plugins)
                        .then(() => {
                            resolve();
                        });
                })
        });

    }

    registerPlugins() {
        return new Promise((resolve, reject) => {
            this.bootNextPlugin(this.pluginsAtRegister)
                .then(() => {
                    resolve()
                })
                .catch(() => {
                    reject()
                });
        });
    }

    bootPlugins() {
        return new Promise((resolve, reject) => {
            this.bootNextPlugin(this.pluginsAtBoot)
                .then(() => {
                    resolve()
                })
                .catch(() => {
                    reject()
                });
        });
    }

    runRegister() {
        return new Promise((resolve, reject) => {

            this.logInfo('Registering plugins: ' + this.pluginsAtRegister.map(plugin => plugin.name).join(', '));

            Promise
                .all(this.pluginsAtRegister.map(plugin => plugin(this)))
                .then((status) => {
                    //this.logInfo('Done registering application plugins');
                    resolve(this)
                })
                .catch((err) => {
                    console.error(err);
                    this.logError('Failed to register all application plugins');
                    reject();
                })

        });
    }

    runBoot() {
        return new Promise((resolve, reject) => {

            this.logInfo('Booting plugins: ' + this.pluginsAtBoot.map(plugin => plugin.name).join(', '));

            this.bootPlugins()
                .then(() => {
                    //this.logInfo('Application was booted');
                    resolve(this)
                })
                .catch((err) => {
                    console.error(err);
                    this.logError('Failed to boot all application plugins');
                    reject();
                })

        });
    }

    run() {

        this.logInfo('Starting up application');

        return new Promise((resolve, reject) => {

            Promise.resolve()
                .then(() => this.runRegister())
                .then(() => this.runBoot())
                .then(() => {
                    this.logInfo('Application is running!');
                    resolve(this)
                })
                .catch((err) => {
                    console.error(err);
                    this.logError('Failed to start up all application dependencies');
                    reject();
                });

        });

    }

    static create() {

        /// Create new app instance
        const app = new App;

        /// Register default plugins
        app.register(GlobalAppPlugin);
        app.register(EnvPlugin);
        app.register(MomentPlugin);
        app.register(AxiosPlugin);
        app.register(TitlePlugin);

        /// Apply some extra plugins that should always be booted
        app.boot(DocumentReadyPlugin);
        app.boot(ReactRenderPlugin);

        return app;
    }

}
